import {useAuth} from "../../auth/AuthContext";
import {ProfileApi} from "../../api/ProfileApi";
import {ProductViewModel} from "../../api/models";
import {useAsync} from "../../components/useAsync";

export type DownloadAccess = 'checking' | 'allowed' | 'signed-out' | 'no-license';

// Whether the viewer may download the product's files, as the API decides it: a released product's are for anyone, a
// product in Early Access's for administrators and the owners of a license for it that hasn't been revoked. The API enforces it;
// this only keeps the page from listing files the viewer can't have.
export function useDownloadAccess(product: ProductViewModel | undefined): { access: DownloadAccess, error: unknown } {
    const {user, loading: sessionLoading, isAdmin} = useAuth();
    const needsLicense = !!product?.beta && !!user && !isAdmin;
    const licenses = useAsync(async () => needsLicense ? await ProfileApi.licenses() : undefined, [needsLicense, user?.username]);

    if (!product) {
        return { access: 'checking', error: undefined };
    }
    if (!product.beta) {
        return { access: 'allowed', error: undefined };
    }
    if (sessionLoading) {
        return { access: 'checking', error: undefined };
    }
    if (!user) {
        return { access: 'signed-out', error: undefined };
    }
    if (isAdmin) {
        return { access: 'allowed', error: undefined };
    }
    if (licenses.loading || !licenses.data) {
        return { access: 'checking', error: licenses.error };
    }
    const holds = licenses.data.some(x => x.productId === product.id && !x.revoked);
    return { access: holds ? 'allowed' : 'no-license', error: undefined };
}
